import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as ErrorIcon,
  Science as ScienceIcon,
  Settings as SettingsIcon,
  LinkOff as LinkOffIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import {
  keystoneService,
  KeystoneConfig,
  KeystoneConfigPayload,
} from '../../services/keystoneService';

const KeystoneIntegrationSettings: React.FC = () => {
  const [config, setConfig] = useState<KeystoneConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [unauthorized, setUnauthorized] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  // INSTANTS in the CLINIC zone. ClinicKeystoneConfig.lastConnectedAt/lastErrorAt are
  // stamped `new Date()` server-side (keystone.controller.ts, asteris-integration.service.ts)
  // — DATE-KINDS §1 "Integrations" — so they are moments, never re-zoned into calendar days.
  const formatInstant = useFormatInstant();

  const loadConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnauthorized(false);
    try {
      setConfig(await keystoneService.getConfig());
    } catch (err: any) {
      if (err.response?.status === 403) {
        // VET/TECH users — render an "owners/admins only" stub instead.
        setUnauthorized(true);
      } else {
        setError(err.response?.data?.message || 'Failed to load Keystone configuration');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSaved = (next: KeystoneConfig) => {
    setConfig(next);
    setFormOpen(false);
    setSuccessMessage('Keystone integration connected.');
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleDisconnect = async () => {
    try {
      await keystoneService.deleteConfig();
      setConfig({ configured: false });
      setDisconnectOpen(false);
      setSuccessMessage('Keystone integration disconnected.');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disconnect Keystone integration');
    }
  };

  const isConfigured = config?.configured === true;

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <ScienceIcon color="primary" fontSize="large" />
            <Box>
              <Typography variant="h6">Asteris Keystone</Typography>
              <Typography variant="body2" color="text.secondary">
                Medical imaging integration for X-Ray, Ultrasound, MRI, CT
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {loading ? (
              <CircularProgress size={24} />
            ) : isConfigured ? (
              <Chip
                icon={<CheckIcon />}
                label="Connected"
                color="success"
                variant="outlined"
                size="small"
              />
            ) : (
              <Chip
                icon={<ErrorIcon />}
                label="Not Configured"
                color="default"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {!loading && unauthorized && (
          <Typography variant="body2" color="text.secondary">
            Only clinic owners and admins can configure the Keystone integration.
          </Typography>
        )}

        {!loading && !unauthorized && !isConfigured && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your Asteris-issued credentials to push imaging requests
              and receive studies and reports back into StableTrack.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SettingsIcon />}
              onClick={() => setFormOpen(true)}
            >
              Configure
            </Button>
          </Box>
        )}

        {!loading && !unauthorized && isConfigured && config?.configured && (
          <Box>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Clinic Code
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {config.keystoneClinicCode}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {config.apiUsername}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Last Connected
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  data-testid="keystone-last-connected"
                >
                  {formatInstant(config.lastConnectedAt, INSTANT_FORMATS.dateTimeLong) || '—'}
                </Typography>
              </Box>
              {config.lastError && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Last error: {config.lastError}
                  {config.lastErrorAt && (
                    <Typography
                      variant="caption"
                      component="div"
                      color="text.secondary"
                      data-testid="keystone-last-error-at"
                    >
                      {formatInstant(config.lastErrorAt, INSTANT_FORMATS.dateTimeLong)}
                    </Typography>
                  )}
                </Alert>
              )}
            </Stack>

            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => setFormOpen(true)}
              >
                Reconfigure
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={() => setDisconnectOpen(true)}
              >
                Disconnect
              </Button>
            </Stack>
          </Box>
        )}
      </CardContent>

      <KeystoneConfigDialog
        open={formOpen}
        initial={config?.configured ? config : undefined}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />

      <Dialog open={disconnectOpen} onClose={() => setDisconnectOpen(false)}>
        <DialogTitle>Disconnect Keystone Integration?</DialogTitle>
        <DialogContent>
          <Typography>
            New imaging requests won't be sent to Asteris until you reconnect.
            Past imaging records, studies, and reports will remain viewable.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDisconnect}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

interface KeystoneConfigDialogProps {
  open: boolean;
  initial?: { keystoneClinicCode: string; apiUsername: string };
  onClose: () => void;
  onSaved: (next: KeystoneConfig) => void;
}

const KeystoneConfigDialog: React.FC<KeystoneConfigDialogProps> = ({
  open,
  initial,
  onClose,
  onSaved,
}) => {
  const [clinicCode, setClinicCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset form whenever the dialog opens with fresh `initial` props.
  useEffect(() => {
    if (open) {
      setClinicCode(initial?.keystoneClinicCode ?? '');
      setUsername(initial?.apiUsername ?? '');
      setPassword('');
      setShowPassword(false);
      setTestResult(null);
      setSaveError(null);
    }
  }, [open, initial]);

  const canSubmit =
    clinicCode.trim().length > 0 &&
    username.trim().length > 0 &&
    password.length > 0;

  const buildPayload = (): KeystoneConfigPayload => ({
    keystoneClinicCode: clinicCode.trim(),
    apiUsername: username.trim(),
    apiPassword: password,
  });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setSaveError(null);
    try {
      const result = await keystoneService.testConfig(buildPayload());
      setTestResult({ ok: result.success, message: result.message });
    } catch (err: any) {
      setTestResult({
        ok: false,
        message: err.response?.data?.message || 'Test failed.',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const next = await keystoneService.saveConfig(buildPayload());
      onSaved(next);
    } catch (err: any) {
      setSaveError(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initial ? 'Reconfigure Keystone' : 'Configure Keystone'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use the values Asteris support provided for your clinic. Test the
          connection before saving — credentials are only stored after a
          successful test.
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Clinic Code"
            value={clinicCode}
            onChange={(e) => setClinicCode(e.target.value)}
            placeholder="e.g. STBLTRCK"
            fullWidth
            autoComplete="off"
          />
          <TextField
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="API account username"
            fullWidth
            autoComplete="off"
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={
              initial
                ? 'Re-enter password to update credentials'
                : 'API account password'
            }
            fullWidth
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {testResult && (
            <Alert severity={testResult.ok ? 'success' : 'error'}>
              {testResult.message}
            </Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleTest}
          disabled={!canSubmit || testing || saving}
          variant="outlined"
        >
          {testing ? <CircularProgress size={20} /> : 'Test Connection'}
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSubmit || saving || testing}
          variant="contained"
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeystoneIntegrationSettings;
